// JSON export/import — the replacement for the Swift app's Protobuf/JSON share files, and the
// only backup until cloud sync exists. Photos are NOT included (they stay on the device).
import { useLibrary, type LibraryState } from './library';
import { usePlanner } from './planner';
import { useShopping } from './shopping';
import { useProgress } from './progress';
import { useSettings } from './settings';

export const FORMAT = 'chefi-backup';
export const VERSION = 1;

type LibraryKinds = Pick<LibraryState, 'recipes' | 'cookbooks' | 'mealplans' | 'units' | 'ingredients' | 'utensils'>;
// Diet confirmations travel with the whole-library export below (exportEverything).

export type Backup = {
  format: typeof FORMAT;
  version: number;
  exportedAt: string;
  library: Partial<LibraryKinds>;
  planner?: unknown;
  shopping?: unknown;
  progress?: unknown;
  settings?: unknown;
};

/** Selected library items only (Share), or the whole library. */
export function exportLibrary(selection?: Partial<Record<keyof LibraryKinds, string[]>>): string {
  const s = useLibrary.getState();
  const kinds: (keyof LibraryKinds)[] = ['recipes', 'cookbooks', 'mealplans', 'units', 'ingredients', 'utensils'];
  const library: Partial<LibraryKinds> = {};
  for (const k of kinds) {
    const ids = selection ? selection[k] : Object.keys(s[k]);
    if (!ids?.length) continue;
    (library as Record<string, unknown>)[k] = Object.fromEntries(ids.filter(id => s[k][id]).map(id => [id, s[k][id]]));
  }
  const backup: Backup = { format: FORMAT, version: VERSION, exportedAt: new Date().toISOString(), library };
  return JSON.stringify(backup, null, 2);
}

/** Everything: library, planner, grocery & pantry, meal log (without photo files), settings. */
export function exportEverything(): string {
  const strip = <T extends object>(state: T) => JSON.parse(JSON.stringify(state)) as unknown;
  const backup: Backup = {
    format: FORMAT,
    version: VERSION,
    exportedAt: new Date().toISOString(),
    library: JSON.parse(exportLibrary()).library,
    planner: strip(usePlanner.getState()),
    shopping: strip(useShopping.getState()),
    progress: strip(useProgress.getState()),
    settings: strip(useSettings.getState()),
  };
  return JSON.stringify(backup, null, 2);
}

/**
 * Imports a backup. Library items are merged (same id = replaced). Planner, shopping,
 * progress and settings are replaced wholesale, matching the "newest copy wins" sync rule.
 * Returns the number of library items imported.
 */
export function importBackup(text: string): number {
  const data = JSON.parse(text) as Partial<Backup>;
  if (data.format !== FORMAT || typeof data.version !== 'number') throw new Error('Not a Chefi backup file');
  if (data.version > VERSION) throw new Error('This backup comes from a newer Chefi version');
  const count = useLibrary.getState().importItems(data.library ?? {});
  const take = (v: unknown) => (v && typeof v === 'object' ? (v as Record<string, unknown>) : null);
  const planner = take(data.planner);
  if (planner) usePlanner.setState({ entries: planner.entries as never, schedule: planner.schedule as never, tripDates: planner.tripDates as never, updatedAt: Date.now() });
  const shopping = take(data.shopping);
  // Imported lists count as changed now, so sync uploads them instead of pulling an older cloud copy over them.
  if (shopping) {
    const now = Date.now();
    useShopping.setState({ grocery: { ...(shopping.grocery as object), updatedAt: now } as never, pantry: { ...(shopping.pantry as object), updatedAt: now } as never });
  }
  const progress = take(data.progress);
  if (progress) {
    useProgress.setState({
      log: (progress.log as never) ?? [], coveredDays: (progress.coveredDays as never) ?? [], freezes: (progress.freezes as number) ?? 0,
      creditedWeeks: (progress.creditedWeeks as never) ?? [], pinnedCookbookId: progress.pinnedCookbookId as string | undefined, updatedAt: Date.now(),
    });
  }
  const settings = take(data.settings);
  if (settings) {
    const { isSuper: _ignored, ...rest } = settings; // entitlement always comes from the store
    useSettings.getState().set(rest as never);
  }
  return count;
}
